import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NotificationService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation/query.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching the notification list.
 *
 * @param {Object} params - Query parameters (page, limit, is_read)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useListNotification = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["notification-list", params],
    queryFn: () => {
      return NotificationService.list(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for polling the logged-in admin's unread notification count, used to drive the
 * header bell badge. Polls every 30s and refetches on window focus so the badge stays current
 * without a real-time socket connection (the admin web panel has none - see AppHeader.js).
 *
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useUnreadNotificationCount = (onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["notification-unread-count"],
    queryFn: () => {
      return NotificationService.getUnreadCount();
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 0,
    cacheTime: 60000,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Custom hook for marking a single notification as read.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useReadOneNotification = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId) => {
      return NotificationService.readOne(notificationId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["notification-list"] });
      queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for marking every notification owned by the logged-in admin as read.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useReadAllNotifications = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      return NotificationService.readAll();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["notification-list"] });
      queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for deleting (soft-deleting) every notification owned by the logged-in admin.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useDeleteAllNotifications = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      return NotificationService.deleteAll();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["notification-list"] });
      queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

export {
  useListNotification,
  useUnreadNotificationCount,
  useReadOneNotification,
  useReadAllNotifications,
  useDeleteAllNotifications,
};
